import { createContext, useContext, type ComponentType, type MouseEvent } from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "../lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

type RailSectionPath =
  | "/"
  | "/$org"
  | "/$org/apps"
  | "/$org/chat"
  | "/$org/compliance"
  | "/$org/inbox"
  | "/$org/settings/$section"
  | "/$org/spaces"
  | "/$org/workflows"
  | "/compliance"
  | "/inbox"
  | "/settings";
const RAIL_TOOLTIP_DELAY = 150;

/**
 * A rail destination. Resting, the glyph is the sidebar's icon ink; hovered,
 * the rail's gliding pill (NavRail's RailHoverPill) sits behind it, so the
 * item itself paints no hover surface; selected, it is a primary button —
 * primary fill, primary-foreground stroke — the one filled thing on the rail.
 */
export const RAIL_ITEM_BASE_CLASS =
  "relative z-[1] flex cursor-pointer items-center justify-center text-[var(--sidebar-icon-color)] outline-hidden ring-ring transition-colors duration-200 hover:text-sidebar-foreground focus-visible:ring-2 data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:shadow-xs data-[active=true]:hover:text-primary-foreground";

/** Lets a rail item tell the rail which element the hover pill should glide to. */
export const RailHoverContext = createContext<((element: HTMLElement) => void) | null>(null);

export function NavRailItem({
  label,
  icon: Icon,
  to,
  isActive,
  bigger,
  params,
  tourId,
  count = 0,
  countLabel = "items",
  onIntent,
  onClick,
  expanded,
}: {
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** A destination; without one the item is a button (More, Workflows) and needs `onClick`. */
  to?: RailSectionPath | undefined;
  onClick?: (() => void) | undefined;
  /** A disclosure item's state (More / Less). */
  expanded?: boolean | undefined;
  params?: { readonly org: string; readonly section?: string } | undefined;
  isActive: boolean;
  /** Settings → General → Bigger icons: a wider rail with larger glyphs. */
  bigger: boolean;
  tourId?: string | undefined;
  count?: number;
  countLabel?: string;
  onIntent?: (() => void) | undefined;
}) {
  const hoverRail = useContext(RailHoverContext);
  const shared = {
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      // The pill glides behind the icon tile, not the whole labelled item.
      hoverRail?.(
        event.currentTarget.querySelector<HTMLElement>("[data-rail-tile]") ?? event.currentTarget,
      );
      onIntent?.();
    },
    onFocus: onIntent,
    "aria-label": count > 0 ? `${label}, ${count} ${countLabel}` : label,
    "data-tour": tourId,
    className: RAIL_LABELLED_ITEM_CLASS,
  };
  const content = (
    <>
      <span
        data-rail-tile=""
        data-active={isActive}
        className={cn(
          RAIL_ITEM_BASE_CLASS,
          "group-hover:text-sidebar-foreground",
          bigger ? "size-9 rounded-lg" : "size-8 rounded-[var(--control-radius)]",
        )}
      >
        <NavRailIconWithCount icon={Icon} iconClassName={bigger ? "size-5" : "size-4"} count={count} />
      </span>
      <NavRailLabel active={isActive}>{label}</NavRailLabel>
    </>
  );
  return (
    <Tooltip>
      <TooltipTrigger
        delay={RAIL_TOOLTIP_DELAY}
        render={
          to === undefined ? (
            <button type="button" {...shared} aria-expanded={expanded} onClick={onClick} data-rail-button="">
              {content}
            </button>
          ) : (
            <Link
              to={to}
              {...shared}
              {...(params ? { params } : {})}
              aria-current={isActive ? "page" : undefined}
              data-rail-link=""
            >
              {content}
            </Link>
          )
        }
      />
      <TooltipPopup side="right">{label}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * A labelled rail item: the icon tile with its name beneath, the whole column
 * one link. Focus draws on the tile, where the eye already is.
 */
export const RAIL_LABELLED_ITEM_CLASS =
  "group relative z-[1] flex w-full cursor-pointer flex-col items-center gap-1 py-0.5 outline-hidden focus-visible:[&>[data-rail-tile]]:ring-2 focus-visible:[&>[data-rail-tile]]:ring-ring";

/**
 * The name under a rail icon: small, medium weight, the app font. Muted at
 * rest, foreground on the selected item. Sized so "Governance" fits the rail
 * without truncating.
 */
export function NavRailLabel({ active, children }: { active: boolean; children: string }) {
  return (
    <span
      data-rail-label=""
      className={cn(
        "max-w-full whitespace-nowrap text-[10px] font-medium leading-none tracking-[-0.01em] transition-colors duration-200",
        active
          ? "text-sidebar-foreground"
          : "text-sidebar-muted-foreground group-hover:text-sidebar-foreground",
      )}
    >
      {children}
    </span>
  );
}

/**
 * The glyph and its counter as one unit.
 *
 * The badge anchors to this wrapper, which is exactly the size of the icon —
 * not to the enclosing button, whose padding box pushed the count out into
 * the empty corner of the ghost target.
 */
export function NavRailIconWithCount({
  icon: Icon,
  iconClassName,
  count = 0,
}: {
  readonly icon: ComponentType<{ className?: string }>;
  readonly iconClassName: string;
  readonly count?: number;
}) {
  return (
    <span data-rail-icon="" className="relative inline-flex shrink-0 items-center justify-center">
      <Icon className={iconClassName} />
      <NavRailCountBadge count={count} />
    </span>
  );
}

/**
 * A compact counter, sized for rail icons.
 *
 * Purpose-built rather than the shared `Badge`: that component's `size`
 * variants carry their own height, min-width and padding, so overriding them
 * into a 16px counter needed `!` escapes and still produced an oval. Here the
 * geometry is the component's own — a single digit is an exact circle
 * (`size-4` + `aspect-square`, no padding), and only wider counts grow
 * sideways on `px-1`, keeping fully round ends.
 *
 * Positioned against the icon wrapper above, so it straddles the glyph's
 * top-right corner; the sidebar-coloured ring keeps it legible in both themes.
 */
export function NavRailCountBadge({ count }: { readonly count: number }) {
  if (count <= 0) return null;
  const label = count > 99 ? "99+" : String(count);
  return (
    <span
      aria-hidden
      data-rail-count=""
      className={cn(
        "pointer-events-none absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--brand-accent)] text-[10px] font-semibold leading-none tabular-nums text-[var(--brand-accent-foreground)] ring-2 ring-sidebar",
        // The glyph's own hover cue: the counter lifts with the rail item and
        // nothing around it moves.
        "transition-transform duration-150 ease-out group-hover:scale-110 motion-reduce:transition-none",
        label.length === 1 ? "aspect-square px-0" : "px-1",
      )}
    >
      {/* Optical centring, measured by the browser rather than by us. With
          `leading-none` the grid centres the LINE box, but a digit's ink runs
          from the baseline up to the cap height, which sits above that centre
          by an amount that depends on the font — a fixed pixel nudge
          calibrated on one font is wrong on the next (macOS resolves
          `--font-sans` to SF, Linux to whatever is installed). `text-box`
          trims the line box down to exactly cap-height-to-baseline, so what
          the grid centres IS the ink. Browsers without it fall back to the
          plain centring, which is the pre-existing behaviour. */}
      <span className="block [text-box:trim-both_cap_alphabetic]">{label}</span>
    </span>
  );
}
